import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  TextField,
  TablePagination,
} from '@mui/material';
import DermoMedicineForm from './DermoMedicineForm';
import ConfirmationDialog from '../components/ConfirmationDialog';

const DermoMedicineList = () => {
  const [medicines, setMedicines] = useState([]);
  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [medicineToDelete, setMedicineToDelete] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentMedicine, setCurrentMedicine] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchMedicines();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredMedicines(medicines);
      return;
    }

    const filtered = medicines.filter((med) =>
      (med.specialite && med.specialite.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (med.labo && med.labo.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (med.gamme && med.gamme.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    setFilteredMedicines(filtered);
  }, [searchQuery, medicines]);

  const fetchMedicines = async () => {
    try {
      const response = await axios.get("/dermo-medicines", {
        headers: { 
          "x-access-token": localStorage.getItem("token") || sessionStorage.getItem('token'),
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      setMedicines(response.data.medicines);
      setFilteredMedicines(response.data.medicines);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setCurrentMedicine(null);
    setOpen(true);
  };

  const handleUpdateClick = (medicine) => {
    setCurrentMedicine(medicine);
    setOpen(true);
  };

  const handleDeleteClick = (id) => {
    setMedicineToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
  
    if (!medicineToDelete) return;
  
    setMedicines((prev) => prev.filter((med) => med._id !== medicineToDelete));
    setFilteredMedicines((prev) => prev.filter((med) => med._id !== medicineToDelete));
  
    try {
      await axios.delete(`/dermo-medicines/${medicineToDelete}`, {
        headers: {
          "x-access-token": localStorage.getItem("token") || sessionStorage.getItem("token"),
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
    } catch (error) {
      console.error("Error deleting medicine:", error);
      alert("Failed to delete medicine. Please try again.");
      fetchMedicines();
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentMedicine(null);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box padding={2}>
      <Typography variant={isMobile ? "h4" : "h3"} gutterBottom>
        Dermo Medicines
      </Typography>
      <Button variant="contained" color="primary" onClick={handleAddClick} sx={{ mb: 2 }}>
        Add Dermo Medicine
      </Button>
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchQuery}
        onChange={handleSearchChange}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.grey[200] }}>
              <TableRow>
                <TableCell>Specialite</TableCell>
                <TableCell>Labo</TableCell>
                <TableCell>Gamme</TableCell>
                <TableCell>Forme</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMedicines
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((med) => (
                  <TableRow key={med._id}>
                    <TableCell>{med.specialite}</TableCell>
                    <TableCell>{med.labo}</TableCell>
                    <TableCell>{med.gamme}</TableCell>
                    <TableCell>{med.forme}</TableCell>
                    <TableCell align="right">
                      <ButtonGroup variant="outlined" size="small">
                        <Button onClick={() => handleUpdateClick(med)} color="primary">
                          Update
                        </Button>
                        <Button onClick={() => handleDeleteClick(med._id)} color="error">
                          Delete
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredMedicines.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      )}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this dermo medicine?"
        confirmText="Delete"
        confirmColor="error"
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{currentMedicine ? 'Update Dermo Medicine' : 'Add Dermo Medicine'}</DialogTitle>
        <DialogContent>
          <DermoMedicineForm
            medicine={currentMedicine}
            onClose={handleClose}
            onSave={fetchMedicines}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DermoMedicineList; 